<template>
  <div class="login-container">
    <div class="login-box">
      <h2 class="login-title">网课销售管理系统</h2>
      <form class="login-form" @submit.prevent="handleLogin">
        <div class="form-item">
          <div class="form-row">
            <label class="form-label">账号</label>
            <input
              v-model="loginForm.account"
              type="text"
              class="form-input"
              placeholder="请输入账号（用户名或手机号）"
              required
            />
          </div>
          <span v-if="errors.account" class="error-message">{{ errors.account }}</span>
        </div>

        <div class="form-item">
          <div class="form-row">
            <label class="form-label">密码</label>
            <div class="password-wrapper">
              <input
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input password-input"
                placeholder="请输入密码"
                required
                @keyup.enter="handleLogin"
              />
              <span class="password-toggle" @click="showPassword = !showPassword">
                {{ showPassword ? '🙈' : '👁️' }}
              </span>
            </div>
          </div>
          <span v-if="errors.password" class="error-message">{{ errors.password }}</span>
        </div>

        <div class="form-item">
          <div class="form-row">
            <label class="form-label">验证码</label>
            <div class="captcha-wrapper">
              <input
                v-model="loginForm.captcha"
                type="text"
                class="form-input captcha-input"
                placeholder="请输入计算结果"
                required
                @keyup.enter="handleLogin"
                @input="clearCaptchaError"
              />
              <div class="math-captcha" @click="generateCaptcha">
                <span class="captcha-decor-left"></span>
                <span class="captcha-expression" v-html="formatCaptchaExpression"></span>
                <span class="captcha-refresh-icon">↻</span>
                <span class="captcha-decor-right"></span>
              </div>
            </div>
          </div>
          <span v-if="errors.captcha" class="error-message">{{ errors.captcha }}</span>
        </div>

        <div class="form-item remember-item">
          <label class="remember-me">
            <input
              v-model="rememberMe"
              type="checkbox"
              class="remember-checkbox"
            />
            <span class="remember-text">记住我</span>
          </label>
        </div>

        <div class="form-item">
          <button
            type="submit"
            class="login-button"
            :disabled="loading"
          >
            {{ loading ? '登录中...' : '登录' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { adminLogin } from '@/api/login'
import JSONBig from 'json-bigint'
import { getStorage, setStorage, removeStorage, STORAGE_KEYS } from '@/utils/storage'
import { handleApiError } from '@/utils/errorHandler'

// 使用json-bigint处理BigInt序列化
const JSONBigNative = JSONBig({ useNativeBigInt: true })

export default {
  name: 'LoginPage',
  data() {
    return {
      loginForm: {
        account: '',
        password: '',
        captcha: ''
      },
      errors: {},
      captchaExpression: '', // 验证码表达式，如 "3 + 5 = ?"
      captchaAnswer: 0, // 验证码正确答案
      loading: false,
      rememberMe: false, // 记住我选项
      showPassword: false // 密码可见性
    }
  },
  computed: {
    // 格式化验证码表达式，为数字添加颜色
    formatCaptchaExpression() {
      if (!this.captchaExpression) {
        return '点击生成验证码'
      }
      // 解析表达式，例如 "3 - 3"
      const match = this.captchaExpression.match(/(\d+)\s*([+\-*/])\s*(\d+)/)
      if (match) {
        const num1 = match[1]
        const operator = match[2]
        const num2 = match[3]
        // 优化算式文本颜色，提升识别度（使用内联样式确保颜色生效）
        return `<span style="color: #FFFFFF; font-weight: 700; text-shadow: 0 1px 2px rgba(0,0,0,0.2);">${num1}</span><span style="color: #FDE68A; font-weight: 600;">${operator}</span><span style="color: #FFFFFF; font-weight: 700; text-shadow: 0 1px 2px rgba(0,0,0,0.2);">${num2}</span>`
      }
      return this.captchaExpression
    }
  },
  mounted() {
    // 页面加载时，从localStorage读取保存的账号和密码
    this.loadRememberedCredentials()
    // 生成初始验证码
    this.generateCaptcha()
  },
  methods: {
    // 生成数学运算验证码（加减乘除）
    generateCaptcha() {
      // 随机选择运算符：+、-、*、/
      const operators = ['+', '-', '*', '/']
      const operator = operators[Math.floor(Math.random() * operators.length)]
      
      let num1, num2, answer
      
      if (operator === '/') {
        // 除法：确保结果是整数
        num2 = Math.floor(Math.random() * 9) + 1 // 1-9
        answer = Math.floor(Math.random() * 10) + 1 // 1-10
        num1 = num2 * answer
      } else if (operator === '-') {
        // 减法：确保结果为正数
        num1 = Math.floor(Math.random() * 20) + 10 // 10-29
        num2 = Math.floor(Math.random() * num1) // 0到num1-1
        answer = num1 - num2
      } else if (operator === '*') {
        // 乘法：使用较小的数字
        num1 = Math.floor(Math.random() * 9) + 1 // 1-9
        num2 = Math.floor(Math.random() * 9) + 1 // 1-9
        answer = num1 * num2
      } else {
        // 加法
        num1 = Math.floor(Math.random() * 20) + 1 // 1-20
        num2 = Math.floor(Math.random() * 20) + 1 // 1-20
        answer = num1 + num2
      }
      
      // 生成表达式（不包含等号和问号）
      this.captchaExpression = `${num1} ${operator} ${num2}`
      // 保存正确答案
      this.captchaAnswer = answer
      // 清除之前的验证码输入
      this.loginForm.captcha = ''
      // 清除验证码错误
      this.clearCaptchaError()
    },
    
    // 加载保存的账号和密码
    loadRememberedCredentials() {
      const savedAccount = getStorage(STORAGE_KEYS.REMEMBERED_ACCOUNT)
      const savedPassword = getStorage(STORAGE_KEYS.REMEMBERED_PASSWORD)
      
      if (savedAccount && savedPassword) {
        this.loginForm.account = savedAccount
        this.loginForm.password = savedPassword
        this.rememberMe = true
      }
    },
    
    // 保存账号和密码到localStorage
    saveRememberedCredentials() {
      if (this.rememberMe) {
        setStorage(STORAGE_KEYS.REMEMBERED_ACCOUNT, this.loginForm.account)
        setStorage(STORAGE_KEYS.REMEMBERED_PASSWORD, this.loginForm.password)
      } else {
        // 如果未勾选记住我，清除保存的信息
        removeStorage(STORAGE_KEYS.REMEMBERED_ACCOUNT)
        removeStorage(STORAGE_KEYS.REMEMBERED_PASSWORD)
      }
    },
    
    // 清空验证码错误
    clearCaptchaError() {
      if (this.errors.captcha) {
        delete this.errors.captcha
      }
    },
    
    // 将对象中的BigInt转换为字符串，用于安全序列化
    sanitizeBigInt(obj) {
      if (obj === null || obj === undefined) {
        return obj
      }
      if (typeof obj === 'bigint') {
        return obj.toString()
      }
      if (Array.isArray(obj)) {
        return obj.map(item => this.sanitizeBigInt(item))
      }
      if (typeof obj === 'object') {
        const result = {}
        for (const key in obj) {
          if (Object.prototype.hasOwnProperty.call(obj, key)) {
            result[key] = this.sanitizeBigInt(obj[key])
          }
        }
        return result
      }
      return obj
    },
    
    // 表单验证
    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.loginForm.account) {
        this.errors.account = '请输入账号'
        isValid = false
      }

      if (!this.loginForm.password) {
        this.errors.password = '请输入密码'
        isValid = false
      } else if (this.loginForm.password.length < 6) {
        this.errors.password = '密码长度不能少于6位'
        isValid = false
      }

      if (!this.loginForm.captcha) {
        this.errors.captcha = '请输入验证码'
        isValid = false
      } else {
        // 验证验证码答案（转换为数字比较）
        const userAnswer = parseInt(this.loginForm.captcha.trim())
        if (isNaN(userAnswer) || userAnswer !== this.captchaAnswer) {
          this.errors.captcha = '验证码不正确'
          isValid = false
        }
      }

      return isValid
    },
    
    // 显示消息提示
    showMessage(message, type = 'info') {
      const messageEl = document.createElement('div')
      messageEl.className = `message message-${type}`
      messageEl.textContent = message
      messageEl.style.cssText = `
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        padding: 12px 24px;
        background: ${type === 'success' ? '#67c23a' : type === 'error' ? '#f56c6c' : '#409eff'};
        color: white;
        border-radius: 4px;
        box-shadow: 0 2px 12px rgba(0,0,0,0.1);
        z-index: 9999;
        animation: slideDown 0.3s;
      `
      document.body.appendChild(messageEl)
      setTimeout(() => {
        messageEl.style.animation = 'slideUp 0.3s'
        setTimeout(() => {
          document.body.removeChild(messageEl)
        }, 300)
      }, 3000)
    },
    
    // 登录处理
    async handleLogin() {
      // 先验证表单
      if (!this.validateForm()) {
        return false
      }

      this.loading = true
      try {
        const response = await adminLogin({
          account: this.loginForm.account,
          password: this.loginForm.password
        })

        if (response.data && response.data.success) {
          const userData = response.data.data
          
          // 保存token到localStorage
          if (userData.token) {
            setStorage(STORAGE_KEYS.TOKEN, userData.token)
          }
          
          // 保存用户信息的各个字段到localStorage
          if (userData.id !== undefined) {
            setStorage(STORAGE_KEYS.USER_ID, String(userData.id))
          }
          if (userData.username) {
            setStorage(STORAGE_KEYS.USERNAME, userData.username)
          }
          if (userData.nickName) {
            setStorage(STORAGE_KEYS.NICK_NAME, userData.nickName)
          }
          if (userData.avatar) {
            setStorage(STORAGE_KEYS.AVATAR, userData.avatar)
          }
          if (userData.roleType !== undefined) {
            setStorage(STORAGE_KEYS.ROLE_TYPE, String(userData.roleType))
          }
          if (userData.phone) {
            setStorage(STORAGE_KEYS.PHONE, userData.phone)
          }
          if (userData.email) {
            setStorage(STORAGE_KEYS.EMAIL, userData.email)
          }
          if (userData.status !== undefined) {
            setStorage(STORAGE_KEYS.STATUS, String(userData.status))
          }
          if (userData.empName) {
            setStorage(STORAGE_KEYS.EMP_NAME, userData.empName)
          }
          
          // 保存完整的用户信息对象（使用json-bigint处理BigInt序列化）
          try {
            setStorage(STORAGE_KEYS.USER_INFO, JSONBigNative.stringify(userData))
          } catch (e) {
            console.warn('保存用户信息失败:', e)
            // 如果序列化失败，尝试将BigInt转换为字符串
            const safeUserData = this.sanitizeBigInt(userData)
            setStorage(STORAGE_KEYS.USER_INFO, JSON.stringify(safeUserData))
          }
          
          // 处理记住我功能
          this.saveRememberedCredentials()

          this.showMessage('登录成功', 'success')
          
          // 登录成功后跳转到首页
          const targetPath = '/'
          
          // 使用 replace 避免在历史记录中留下登录页
          setTimeout(() => {
            this.$router.replace(targetPath).catch(err => {
              // 忽略重复导航错误
              if (err.name !== 'NavigationDuplicated') {
                console.error('路由跳转错误:', err)
              }
            })
          }, 500)
        } else {
          this.showMessage(response.data?.message || '登录失败', 'error')
          // 刷新验证码
          this.generateCaptcha()
        }
      } catch (error) {
        handleApiError(error, '登录失败，请稍后重试', { showMessage: true })
        let errorMsg = '登录失败，请稍后重试'
        
        // 处理连接错误
        const baseURL = process.env.NODE_ENV === 'production' 
          ? 'http://106.75.168.72:8840' 
          : 'http://localhost:8840'
        if (error.code === 'ERR_NETWORK' || error.message.includes('ERR_CONNECTION_REFUSED')) {
          errorMsg = `无法连接到服务器，请检查后端服务是否已启动（${baseURL}）`
        } else if (error.response?.data?.message) {
          errorMsg = error.response.data.message
        } else if (error.message) {
          errorMsg = error.message
        }
        
        this.showMessage(errorMsg, 'error')
        // 刷新验证码
        this.generateCaptcha()
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>

.captcha-decor-left {
  position: absolute;
  left: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}

.captcha-expression {
  font-size: 18px;
  font-weight: bold;
  line-height: 1;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
}

.captcha-expression .captcha-num1 {
  color: #ffc53d !important;
}

.captcha-expression .captcha-operator {
  color: #ff4d4f !important;
}

.captcha-expression .captcha-num2 {
  color: #ff4d4f !important;
}

.captcha-refresh-icon {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 4px;
  transition: transform 0.3s;
}

.form-row .captcha-wrapper .math-captcha:hover .captcha-refresh-icon {
  transform: rotate(180deg);
  color: #fff;
}

.captcha-decor-right {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%) rotate(-45deg);
  width: 10px;
  height: 2px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 1px;
}

.error-message {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-image: url('../images/login_backgroud.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  overflow: hidden;
}


.login-box {
  width: 480px;
  padding: 50px 45px;
  background: rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 1;
  animation: fadeInUp 0.6s ease-out;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5),
    inset 0 -1px 0 rgba(255, 255, 255, 0.2);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-title {
  text-align: center;
  margin-bottom: 40px;
  color: #303133;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: 1px;
  background: linear-gradient(135deg, #2563EB 0%, #3B82F6 50%, #60A5FA 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 2px 8px rgba(59, 130, 246, 0.4)) drop-shadow(0 0 20px rgba(96, 165, 250, 0.3));
  position: relative;
}

.login-title::after {
  content: '';
  position: absolute;
  bottom: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #3B82F6, transparent);
  border-radius: 2px;
  opacity: 0.8;
}

.login-form {
  margin-top: 20px;
}

.form-item {
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.form-label {
  min-width: 60px;
  color: #374151;
  font-weight: 500;
  font-size: 14px;
  text-align: right;
}

.form-input {
  flex: 1;
  height: 44px;
  padding: 0 16px;
  border: 1px solid rgba(156, 163, 175, 0.4);
  border-radius: 8px;
  font-size: 14px;
  color: #1f2937;
  transition: all 0.3s;
  box-sizing: border-box;
  background: rgba(255, 255, 255, 0.4);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.form-input:hover {
  border-color: rgba(156, 163, 175, 0.6);
  background: rgba(255, 255, 255, 0.5);
}

.form-input:focus {
  outline: none;
  border-color: rgba(59, 130, 246, 0.5);
  background: rgba(255, 255, 255, 0.6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.form-input::placeholder {
  color: #c0c4cc;
}

.password-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
}

.password-input {
  padding-right: 40px;
}

.password-toggle {
  position: absolute;
  right: 12px;
  cursor: pointer;
  user-select: none;
  font-size: 18px;
  z-index: 10;
  transition: transform 0.2s;
}

.password-toggle:hover {
  transform: scale(1.1);
}

.form-row .captcha-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0; /* 防止flex子元素溢出 */
}

.captcha-input {
  flex: 1;
  min-width: 0; /* 防止输入框溢出 */
  border: 1.5px solid rgba(96, 165, 250, 0.5) !important;
  background: rgba(255, 255, 255, 0.5) !important;
}

.captcha-input:hover {
  border-color: rgba(96, 165, 250, 0.7) !important;
  background: rgba(255, 255, 255, 0.6) !important;
}

.captcha-input:focus {
  border-color: rgba(59, 130, 246, 0.8) !important;
  background: rgba(255, 255, 255, 0.7) !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2) !important;
}

.form-row .captcha-wrapper .math-captcha {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  min-width: 140px;
  width: 140px;
  height: 44px;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.4);
  flex-shrink: 0;
}

.form-row .captcha-wrapper .math-captcha:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.5);
  background: linear-gradient(135deg, #2563EB 0%, #3B82F6 100%);
}

.form-row .captcha-wrapper .math-captcha:active {
  transform: translateY(0);
}

.error-message {
  display: block;
  margin-top: 5px;
  color: #f56c6c;
  font-size: 12px;
}

.login-button {
  width: 100%;
  height: 48px;
  background: linear-gradient(135deg, #2563EB 0%, #3B82F6 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 
    0 4px 15px rgba(37, 99, 235, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.login-button::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.25);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.login-button:hover:not(:disabled) {
  background: linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%);
  transform: translateY(-2px);
  box-shadow: 
    0 8px 25px rgba(37, 99, 235, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

.login-button:hover:not(:disabled)::before {
  width: 300px;
  height: 300px;
}

.login-button:active:not(:disabled) {
  background: linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%);
  transform: translateY(0);
  box-shadow: 
    0 2px 10px rgba(37, 99, 235, 0.4),
    inset 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.1s;
}

.login-button:disabled {
  background: #c0c4cc;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.remember-item {
  margin-bottom: 15px;
}

.remember-me {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.remember-checkbox {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  cursor: pointer;
  accent-color: #3B82F6;
}

.remember-text {
  font-size: 14px;
  color: #374151;
  transition: color 0.3s;
}

.remember-me:hover .remember-text {
  color: #3B82F6;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

@keyframes slideUp {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-20px);
  }
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .login-container {
    padding: 20px;
    min-height: 100vh;
    min-height: -webkit-fill-available; /* iOS Safari 适配 */
  }

  .login-box {
    width: 100%;
    max-width: 100%;
    padding: 30px 20px;
    border-radius: 12px;
  }

  .login-title {
    font-size: 24px;
    margin-bottom: 30px;
  }

  .form-item {
    margin-bottom: 20px;
  }

  .form-label {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .form-input {
    font-size: 16px; /* 防止 iOS 自动缩放 */
    padding: 12px 15px;
    height: 48px; /* 增大点击区域 */
  }

  .password-wrapper {
    position: relative;
  }

  .password-toggle {
    right: 12px;
    font-size: 18px;
  }

  .captcha-wrapper {
    width: 100%;
  }

  .math-captcha {
    padding: 10px 15px;
    font-size: 14px;
  }

  .captcha-expression {
    font-size: 16px;
  }

  .captcha-input {
    font-size: 16px; /* 防止 iOS 自动缩放 */
    padding: 10px 12px;
    height: 44px;
  }

  .login-button {
    width: 100%;
    height: 48px;
    font-size: 16px;
    margin-top: 20px;
  }

  .remember-me {
    font-size: 13px;
  }

  .remember-checkbox {
    width: 18px;
    height: 18px;
  }
}

@media screen and (max-width: 480px) {
  .login-box {
    padding: 25px 15px;
  }

  .login-title {
    font-size: 20px;
    margin-bottom: 25px;
  }

  .form-input {
    padding: 10px 12px;
    height: 44px;
  }

  .login-button {
    height: 44px;
    font-size: 15px;
  }
}

/* 横屏适配 */
@media screen and (max-width: 768px) and (orientation: landscape) {
  .login-container {
    padding: 10px;
  }

  .login-box {
    padding: 20px;
    max-width: 500px;
  }

  .login-title {
    font-size: 20px;
    margin-bottom: 20px;
  }

  .form-item {
    margin-bottom: 15px;
  }
}
</style>